import { useState, useEffect } from 'react';
import api from '@/api';
import { Shield, AlertTriangle, CheckCircle2, Lock, Loader2, RefreshCw, Zap, Target, Users, Activity, Trash2, LogOut, AlertOctagon, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const SEVERITY_STYLE = {
  critical: { color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/50', label: 'CRITICAL' },
  warning:  { color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/10 border-[#3B82F6]/30', label: 'WARNING' },
  info:     { color: 'text-blue-400', bg: 'bg-blue-900/20 border-blue-800/50', label: 'INFO' },
};

const EVENT_ICONS = {
  canary_endpoint_accessed: '🍯',
  honeypot_path_hit: '🍯',
  ip_auto_banned: '🚫',
  ip_blocked: '🚫',
  ip_cleared_manual: '✅',
  account_lockout: '🔒',
  account_unlocked_manual: '🔓',
  new_device_login: '📱',
  ip_unblocked_manual: '🔓',
  sqli_attempt: '💉',
  force_logout: '⏏️',
  emergency_reset: '🚨',
};

export default function SecurityCenter() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [ipReputation, setIpReputation] = useState([]);
  const [targetedPaths, setTargetedPaths] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [lockouts, setLockouts] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [seeding, setSeeding] = useState(false);
  const [severity, setSeverity] = useState('');
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [emergencyLoading, setEmergencyLoading] = useState(false);

  useEffect(() => { load(); }, [severity, tab]);

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, eRes] = await Promise.all([
        api.get('/security/stats'),
        api.get('/security/events', { params: { severity: severity || undefined, limit: 100 } }),
      ]);
      setStats(sRes.data);
      setEvents(eRes.data);

      // Load tab-specific data
      if (tab === 'reputation') {
        const res = await api.get('/security/ip-reputation', { params: { limit: 100 } });
        setIpReputation(res.data);
      } else if (tab === 'paths') {
        const res = await api.get('/security/targeted-paths', { params: { days: 7, limit: 20 } });
        setTargetedPaths(res.data);
      } else if (tab === 'sessions') {
        const res = await api.get('/security/active-sessions');
        setSessions(res.data);
      } else if (tab === 'lockouts') {
        const res = await api.get('/security/account-lockouts');
        setLockouts(res.data);
      } else if (tab === 'audit') {
        const res = await api.get('/security/admin-audit', { params: { limit: 100 } });
        setAuditLog(res.data);
      }
    } catch (e) {
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const clearIpReputation = async (ip) => {
    try {
      await api.post(`/security/ip-reputation/${encodeURIComponent(ip)}/clear`);
      toast.success(`IP ${ip} cleared`);
      load();
    } catch (e) {
      toast.error('Failed to clear IP');
    }
  };

  const banIp = async (ip) => {
    const hours = prompt(`Ban ${ip} for how many hours?`, '24');
    if (!hours) return;
    const reason = prompt('Reason for ban:', 'manual_ban');
    if (reason === null) return;
    try {
      await api.post(`/security/ip-reputation/${encodeURIComponent(ip)}/ban`, null, { 
        params: { hours: parseInt(hours) || 24, reason: reason || 'manual_ban' } 
      });
      toast.success(`IP ${ip} banned for ${hours} hours`);
      load();
    } catch (e) {
      toast.error('Failed to ban IP');
    }
  };

  const unlockAccount = async (email) => {
    try {
      await api.delete(`/security/account-lockouts/${encodeURIComponent(email)}`);
      toast.success(`Account ${email} unlocked`);
      load();
    } catch (e) {
      toast.error('Failed to unlock account');
    }
  };

  const forceLogout = async (userId, email) => {
    if (!confirm(`Force logout all sessions for ${email}?`)) return;
    try {
      await api.post(`/security/force-logout/${userId}`);
      toast.success(`All sessions for ${email} invalidated`);
      load();
    } catch (e) {
      toast.error('Failed to force logout');
    }
  };

  const terminateSession = async (sessionId, email) => {
    if (!confirm(`Terminate this session for ${email}?`)) return;
    try {
      await api.delete(`/security/sessions/${sessionId}`);
      toast.success(`Session terminated for ${email}`);
      load();
    } catch (e) {
      toast.error('Failed to terminate session');
    }
  };

  const emergencyReset = async () => {
    setEmergencyLoading(true);
    try {
      const res = await api.post('/security/emergency-reset');
      toast.success(`Emergency reset complete! Cleared: ${res.data.cleared.ip_bans} IP bans, ${res.data.cleared.account_locks} account locks`);
      setEmergencyOpen(false);
      load();
    } catch (e) {
      toast.error('Emergency reset failed');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const seedCanary = async () => {
    setSeeding(true);
    try {
      await api.post('/security/seed-canary');
      toast.success('Canary traps deployed!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSeeding(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Security Events', icon: Activity },
    { id: 'reputation', label: 'IP Threat Scores', icon: Target },
    { id: 'paths', label: 'Targeted Paths', icon: AlertTriangle },
    { id: 'sessions', label: 'Active Sessions', icon: Users },
    { id: 'lockouts', label: 'Account Lockouts', icon: Lock },
    { id: 'audit', label: 'Admin Audit', icon: Shield },
  ];

  return (
    <div className="p-6 space-y-5 animate-fade-in" data-testid="security-center-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// MASTER ADMIN</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl flex items-center gap-3">
            <Shield size={22} className="text-[#3B82F6]" /> Security Center
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEmergencyOpen(true)}
            className="flex items-center gap-2 px-3 py-2 border border-red-800/50 text-red-400 rounded-sm text-xs hover:bg-red-900/20 transition-colors font-['JetBrains_Mono']">
            <AlertOctagon size={12} /> Emergency Reset
          </button>
          <button onClick={seedCanary} disabled={seeding}
            className="flex items-center gap-2 px-3 py-2 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
            <Zap size={12} /> Deploy Canary
          </button>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Login Failures 24h', value: stats.login_failures_24h, color: stats.login_failures_24h > 10 ? 'text-red-400' : 'text-[#3B82F6]' },
            { label: 'Logins Success 24h', value: stats.login_success_24h, color: 'text-[#57EB31]' },
            { label: 'Blocked IPs', value: stats.blocked_ips, color: stats.blocked_ips > 0 ? 'text-red-400' : 'text-[#57EB31]' },
            { label: 'Locked Accts', value: stats.locked_accounts, color: stats.locked_accounts > 0 ? 'text-[#3B82F6]' : 'text-[#57EB31]' },
            { label: 'Critical 7d', value: stats.critical_events_7d, color: stats.critical_events_7d > 0 ? 'text-red-400' : 'text-[#57EB31]' },
            { label: 'Warnings 7d', value: stats.warning_events_7d, color: stats.warning_events_7d > 5 ? 'text-[#3B82F6]' : 'text-[#57EB31]' },
            { label: 'Canary Triggers', value: stats.canary_triggers_7d, color: stats.canary_triggers_7d > 0 ? 'text-red-400 font-bold' : 'text-[#57EB31]' },
          ].map(({ label, value, color }) => (
            <div key={label} className="card-industrial p-4">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#5C6A7A] mb-2">{label}</p>
              <p className={`text-2xl font-['JetBrains_Mono'] font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {stats?.canary_triggers_7d > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-900/30 border border-red-800 rounded-sm">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-['Manrope'] font-bold text-sm">CANARY TRIGGERED — Possible Intrusion Attempt</p>
            <p className="text-red-300 font-['IBM_Plex_Sans'] text-xs mt-0.5">{stats.canary_triggers_7d} canary access(es) in last 7 days. Review event log.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#2D3F5E] overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider font-['JetBrains_Mono'] transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === id ? 'text-[#3B82F6] border-[#3B82F6]' : 'text-[#5C6A7A] border-transparent hover:text-[#B8C0CC]'
            }`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {/* Security Events Tab */}
      {tab === 'overview' && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <select value={severity} onChange={e => setSeverity(e.target.value)}
              className="h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
              <option value="">All Severities</option>
              <option value="critical">Critical Only</option>
              <option value="warning">Warnings</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div className="card-industrial overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D3F5E]">
                  {['TIME','TYPE','SEVERITY','IP','DETAILS'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-12"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
                ) : events.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">
                    <Shield size={24} className="mx-auto mb-2 text-[#57EB31]" /> No security events
                  </td></tr>
                ) : events.map((ev, i) => {
                  const s = SEVERITY_STYLE[ev.severity] || SEVERITY_STYLE.info;
                  return (
                    <tr key={ev.id} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                      style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                      <td className="px-4 py-2.5 text-xs font-['JetBrains_Mono'] text-[#5C6A7A] whitespace-nowrap">
                        {ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-['JetBrains_Mono'] text-[#B8C0CC]">
                        {EVENT_ICONS[ev.event_type] || '📋'} {ev.event_type?.replace(/_/g,' ')}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${s.bg} ${s.color}`}>
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-['JetBrains_Mono'] text-xs text-[#B8C0CC]">{ev.ip}</td>
                      <td className="px-4 py-2.5 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-xs truncate">
                        {Object.entries(ev.details || {}).map(([k,v]) => `${k}: ${v}`).join(' | ').slice(0, 100)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* IP Threat Scores Tab */}
      {tab === 'reputation' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => {
              const ip = prompt('Enter IP address to ban:');
              if (ip) banIp(ip);
            }}
              data-testid="ban-new-ip-btn"
              className="flex items-center gap-2 px-3 py-2 border border-red-800/50 text-red-400 rounded-sm text-xs hover:bg-red-900/20 transition-colors font-['JetBrains_Mono']">
              <Lock size={12} /> Ban New IP
            </button>
          </div>
          <div className="card-industrial overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D3F5E]">
                  {['IP ADDRESS','SCORE','STATUS','BAN COUNT','LAST REASON','LAST SEEN','ACTIONS'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
                ) : ipReputation.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12">
                    <CheckCircle2 size={24} className="mx-auto mb-2 text-[#57EB31]" />
                    <p className="text-[#57EB31] font-['IBM_Plex_Sans'] text-sm">No threat scores recorded</p>
                  </td></tr>
                ) : ipReputation.map((r, i) => {
                  const isBanned = r.ban_status === 'banned' && r.ban_until && new Date(r.ban_until) > new Date();
                  return (
                    <tr key={r.ip} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                      style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm text-white font-bold">{r.ip}</td>
                    <td className="px-4 py-3">
                      <span className={`font-['JetBrains_Mono'] text-sm font-bold ${r.score >= 100 ? 'text-red-400' : r.score >= 50 ? 'text-[#3B82F6]' : 'text-[#57EB31]'}`}>
                        {r.score}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border font-['JetBrains_Mono'] ${
                        isBanned ? 'text-red-400 bg-red-900/20 border-red-800/50' : 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30'
                      }`}>
                        {isBanned ? 'BANNED' : 'CLEAR'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6]">{r.ban_count || 0}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-xs truncate">{r.last_reason || '—'}</td>
                    <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#5C6A7A]">
                      {r.last_seen ? new Date(r.last_seen).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        {!isBanned && (
                          <button onClick={() => banIp(r.ip)}
                            data-testid={`ban-ip-${r.ip}`}
                            className="px-2.5 py-1 text-[9px] font-bold uppercase border border-red-800/40 text-red-400 rounded-sm hover:bg-red-900/20 transition-colors font-['JetBrains_Mono']">
                            Ban
                          </button>
                        )}
                        {(isBanned || r.score > 0) && (
                          <button onClick={() => clearIpReputation(r.ip)}
                            data-testid={`clear-ip-${r.ip}`}
                            className="px-2.5 py-1 text-[9px] font-bold uppercase border border-[#57EB31]/40 text-[#57EB31] rounded-sm hover:bg-[#57EB31]/10 transition-colors font-['JetBrains_Mono']">
                            Clear
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Targeted Paths Tab */}
      {tab === 'paths' && (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['RANK','PATH','HIT COUNT','LAST HIT'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-12"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
              ) : targetedPaths.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-12">
                  <CheckCircle2 size={24} className="mx-auto mb-2 text-[#57EB31]" />
                  <p className="text-[#57EB31] font-['IBM_Plex_Sans'] text-sm">No attacks detected in last 7 days</p>
                </td></tr>
              ) : targetedPaths.map((p, i) => (
                <tr key={p.path} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="px-4 py-3 text-[#3B82F6] font-['JetBrains_Mono'] font-bold">#{i + 1}</td>
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm text-red-400">{p.path}</td>
                  <td className="px-4 py-3">
                    <span className="font-['JetBrains_Mono'] text-lg font-bold text-white">{p.count}</span>
                    <span className="text-[#5C6A7A] text-xs ml-1">hits</span>
                  </td>
                  <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#5C6A7A]">
                    {p.last_hit ? new Date(p.last_hit).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Active Sessions Tab */}
      {tab === 'sessions' && (
        <div className="space-y-4">
          {/* Active Sessions (from new collection) */}
          <div>
            <h3 className="text-[#3B82F6] font-['Manrope'] font-bold text-sm mb-3 flex items-center gap-2">
              <Users size={14} /> Currently Active Sessions
            </h3>
            <div className="card-industrial overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2D3F5E]">
                    {['USER','ROLE','IP','DEVICE','LOGGED IN','LAST ACTIVITY',''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-12"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
                  ) : (!sessions?.active_sessions || sessions.active_sessions.length === 0) ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No active sessions</td></tr>
                  ) : sessions.active_sessions.map((s, i) => (
                    <tr key={s.session_id || i} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                      style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                      <td className="px-4 py-2.5 text-sm text-white font-['IBM_Plex_Sans']">{s.email}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border font-['JetBrains_Mono'] ${
                          s.role === 'master_admin' ? 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30' :
                          s.role === 'customer' ? 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30' :
                          'text-blue-400 bg-blue-900/20 border-blue-800/50'
                        }`}>
                          {s.role?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-['JetBrains_Mono'] text-xs text-[#B8C0CC]">{s.ip}</td>
                      <td className="px-4 py-2.5 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-[150px] truncate">{s.user_agent?.slice(0, 40)}</td>
                      <td className="px-4 py-2.5 text-xs font-['JetBrains_Mono'] text-[#5C6A7A] whitespace-nowrap">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-['JetBrains_Mono'] text-[#5C6A7A] whitespace-nowrap">
                        {s.last_activity ? new Date(s.last_activity).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <button onClick={() => terminateSession(s.session_id, s.email)}
                          className="flex items-center gap-1 px-2 py-1 text-[9px] font-bold uppercase border border-red-800/40 text-red-400 rounded-sm hover:bg-red-900/20 transition-colors font-['JetBrains_Mono']">
                          <LogOut size={10} /> End
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Login Attempts */}
          <div>
            <h3 className="text-[#8B9AAB] font-['Manrope'] font-bold text-sm mb-3 flex items-center gap-2">
              <Activity size={14} /> Recent Login Attempts (24h)
            </h3>
            <div className="card-industrial overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2D3F5E]">
                    {['USER','IP','TIME','STATUS','DEVICE'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-8"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
                  ) : (!sessions?.recent_logins || sessions.recent_logins.length === 0) ? (
                    <tr><td colSpan={5} className="text-center py-8 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No recent logins</td></tr>
                  ) : sessions.recent_logins.slice(0, 20).map((s, i) => (
                    <tr key={s.id || i} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                      style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                      <td className="px-4 py-2 text-sm text-white font-['IBM_Plex_Sans']">{s.email}</td>
                      <td className="px-4 py-2 font-['JetBrains_Mono'] text-xs text-[#B8C0CC]">{s.ip}</td>
                      <td className="px-4 py-2 text-xs font-['JetBrains_Mono'] text-[#5C6A7A] whitespace-nowrap">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border font-['JetBrains_Mono'] ${
                          s.success ? 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30' : 'text-red-400 bg-red-900/20 border-red-800/50'
                        }`}>
                          {s.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-[150px] truncate">{s.user_agent?.slice(0, 40)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Account Lockouts Tab */}
      {tab === 'lockouts' && (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['EMAIL','LOCKED AT','EXPIRES AT','FAIL COUNT',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
              ) : lockouts.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12">
                  <CheckCircle2 size={24} className="mx-auto mb-2 text-[#57EB31]" />
                  <p className="text-[#57EB31] font-['IBM_Plex_Sans'] text-sm">No locked accounts</p>
                </td></tr>
              ) : lockouts.map((l, i) => (
                <tr key={l.email} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm text-[#3B82F6]">{l.email}</td>
                  <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#5C6A7A]">{l.locked_at ? new Date(l.locked_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#3B82F6]">{l.expires_at ? new Date(l.expires_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm text-red-400 font-bold">{l.fail_count || '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => unlockAccount(l.email)}
                      className="flex items-center gap-1 px-2.5 py-1 text-[9px] font-bold uppercase border border-[#57EB31]/40 text-[#57EB31] rounded-sm hover:bg-[#57EB31]/10 transition-colors font-['JetBrains_Mono']">
                      <Unlock size={10} /> Unlock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin Audit Tab */}
      {tab === 'audit' && (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['TIME','ADMIN','ACTION','TARGET','DETAILS','IP'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12"><Loader2 size={20} className="mx-auto text-[#3B82F6] animate-spin" /></td></tr>
              ) : auditLog.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No admin actions logged yet</td></tr>
              ) : auditLog.map((a, i) => (
                <tr key={a.id} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="px-4 py-2.5 text-xs font-['JetBrains_Mono'] text-[#5C6A7A] whitespace-nowrap">
                    {a.created_at ? new Date(a.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-[#3B82F6] font-['IBM_Plex_Sans']">{a.admin_email}</td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] text-[10px] font-bold uppercase rounded font-['JetBrains_Mono']">
                      {a.action?.replace(/_/g,' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-['JetBrains_Mono'] text-xs text-white">{a.target}</td>
                  <td className="px-4 py-2.5 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-xs truncate">
                    {Object.entries(a.details || {}).map(([k,v]) => `${k}: ${v}`).join(' | ').slice(0, 60)}
                  </td>
                  <td className="px-4 py-2.5 font-['JetBrains_Mono'] text-xs text-[#5C6A7A]">{a.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Emergency Reset Dialog */}
      <Dialog open={emergencyOpen} onOpenChange={setEmergencyOpen}>
        <DialogContent className="bg-[#0F172A] border-[#2D3F5E]">
          <DialogHeader>
            <DialogTitle className="text-red-400 font-['Manrope'] flex items-center gap-2">
              <AlertOctagon size={20} /> Emergency Security Reset
            </DialogTitle>
            <DialogDescription className="text-[#8B9AAB]">
              This will immediately clear ALL IP bans, account lockouts, and reset all threat scores to zero.
              Use only in emergency situations. This action is logged and will send a Slack alert.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-sm">
              <p className="text-red-400 font-['JetBrains_Mono'] text-sm font-bold">⚠️ WARNING</p>
              <p className="text-red-300 text-xs mt-1">This action cannot be undone. All security blocks will be removed.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEmergencyOpen(false)} className="border-[#2D3F5E] text-[#8B9AAB]">
              Cancel
            </Button>
            <Button onClick={emergencyReset} disabled={emergencyLoading} className="bg-red-600 hover:bg-red-700 text-white">
              {emergencyLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <AlertOctagon size={14} className="mr-2" />}
              Execute Emergency Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
